import type { SessionStatus } from '@/src/dal/session-dal'

const MS_PER_MINUTE = 60 * 1000

const DEFAULT_SESSION_REMINDER_INTERVAL_DAYS = 7

export interface SessionReminderRecord {
  id?: number
  created: Date
  definition?: { id: number }
  status?: SessionStatus
}

function isValidDate(value: Date): boolean {
  return !Number.isNaN(value.getTime())
}

function getLatestSessionTime(
  sessions: readonly SessionReminderRecord[] | null | undefined,
): number | null {
  if (sessions == null || sessions.length === 0) {
    return null
  }

  let latest: number | null = null
  for (const session of sessions) {
    if (!(session.created instanceof Date) || !isValidDate(session.created)) {
      continue
    }

    const time = session.created.getTime()
    if (latest == null || time > latest) {
      latest = time
    }
  }

  return latest
}

export function findPendingSessionId({
  sessions,
  definitionId,
}: {
  sessions?: readonly SessionReminderRecord[] | null
  definitionId: number
}): number | null {
  const matchingSessions = sessions
    ?.filter(
      (session) =>
        session.definition?.id === definitionId &&
        session.created instanceof Date &&
        isValidDate(session.created),
    )
    .slice()
    .sort((a, b) => b.created.getTime() - a.created.getTime())

  const latestSession = matchingSessions?.[0]
  if (
    latestSession?.status !== 'Active' ||
    latestSession.id == null ||
    !Number.isFinite(latestSession.id) ||
    latestSession.id <= 0
  ) {
    return null
  }

  return latestSession.id
}

/**
 * Returns true when the signed-in user has no recorded sessions, or when the
 * most recent session is older than the configured interval.
 *
 * Whole-day intervals become due at local midnight after that many calendar
 * days. `intervalMinutes` remains available for short testing intervals and
 * takes precedence over `intervalDays` when both are provided.
 *
 * When `definitionId` is set (voice assessment on the dashboard), only those
 * sessions are considered; `Aborted` sessions are ignored so the reminder can
 * return after an abandoned attempt.
 */
export function isSessionReminderDue({
  sessions,
  now = new Date(),
  intervalMinutes,
  intervalDays = DEFAULT_SESSION_REMINDER_INTERVAL_DAYS,
  definitionId,
}: {
  sessions?: readonly SessionReminderRecord[] | null
  now?: Date
  intervalMinutes?: number
  intervalDays?: number
  /** When set, only sessions for this definition id count (e.g. voice assessment). */
  definitionId?: number
}): boolean {
  if (
    !isValidDate(now) ||
    (intervalMinutes != null ? intervalMinutes <= 0 : intervalDays <= 0)
  ) {
    return false
  }

  const baselineSessions =
    sessions == null
      ? null
      : definitionId == null
        ? sessions
        : sessions.filter(
            (session) =>
              session.definition?.id === definitionId &&
              session.status !== 'Aborted',
          )

  const latestSessionTime = getLatestSessionTime(baselineSessions)
  if (latestSessionTime == null) {
    return true
  }

  if (intervalMinutes != null) {
    const elapsedMs = now.getTime() - latestSessionTime
    return elapsedMs >= intervalMinutes * MS_PER_MINUTE
  }

  const reminderDueAt = new Date(latestSessionTime)
  reminderDueAt.setHours(0, 0, 0, 0)
  reminderDueAt.setDate(reminderDueAt.getDate() + intervalDays)

  return now.getTime() >= reminderDueAt.getTime()
}
